import { useEffect, useState } from 'react';
import type { IProductItem, IProductItem2 } from './type';
import { getListProducts, getListUsualProducts } from '@/service/vip';
import type { ProductModule } from '@/components/EcocreditsMonthlyMadness/type';
import { i18nCCYMap, i18nMap } from '@/constants/i18n';
import { EU_SITES, SAVE_SITES } from '@/constants/membersday';

/**
 * @param price 价格
 * @param locale
 * @returns str
 */
export const formatPrice = (price: string, locale: string, hidePrice = false): string => {
    const str = price ? price.replace('.00', '') : '0';
    let newStr = '';

    if (hidePrice) {
        const repStr = parseInt(str) ? parseInt(str).toString() : str;
        for (let i = 0; i < repStr.length; i++) {
            newStr += '?';
        }
    } else {
        newStr = new Intl.NumberFormat(i18nMap[locale]).format(Number(str));

        if (isNaN(Number(str))) newStr = '0';
        if (locale === 'za') newStr = newStr.replace(/\W/g, ',');
        // if (locale === 'de') newStr = `${newStr.replace(/\./g, ' ')} `;
    }
    // 欧洲6国想要加空格
    if (['fr', 'de', 'es', 'it'].includes(locale)) return `${newStr} ${i18nCCYMap[locale]}`;
    // if (locale === 'eu') return `${i18nCCYMap[locale]} ${newStr}`;
    return i18nCCYMap[locale] + newStr;
};

export const encodeProductPrice = (price: string) => {
    const integer = parseInt(price, 10).toString();
    const firstChar = integer.charAt(0);
    return firstChar + '?'.repeat(integer.length - 1);
};

/**
 * 为了和金额一样格式化积分
 * @param points points
 * @param locale
 * @returns str
 */
export const formatPoints = (points: string, locale: string, hidePrice = false): string => {
    const str = points || '0';
    let newStr = '';

    if (hidePrice) {
        for (let i = 0; i < str.length; i++) {
            newStr += '?';
        }
    } else {
        newStr = new Intl.NumberFormat(i18nMap[locale]).format(Number(str));
        if (locale === 'za') newStr = newStr.replace(/\W/g, ',');
        if (locale === 'de') newStr = `${newStr.replace(/\./g, ' ')} `;
    }
    return newStr;
};

/**
 * @param original 划线价
 * @param current 会员价
 * @returns 折扣
 */
export const getDiscount = (original: number, current: number, locale: string, nospace?: boolean): string => {
    if (original === current) return '';
    let off = String(Math.round(((original - current) / original) * 100));
    if (EU_SITES.includes(locale) && !nospace) off += ' ';
    return `${off}%`;
};

export const useProductList = (module: ProductModule, locale: string) => {
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState<IProductItem[]>([]);

    useEffect(() => {
        getListProducts(
            {
                module,
            },
            locale,
        )
            .then((res) => {
                if (res.code !== '0') return;
                res.data?.map((item: IProductItem) => {
                    if (SAVE_SITES.includes(locale)) {
                        if (parseInt(item.compareAtPrice) > parseInt(item.memberPrice)) {
                            item.discount = formatPrice(String(parseInt(item.compareAtPrice) - parseInt(item.memberPrice)), locale);
                            // 不同区域展示有差异
                            if (locale === 'za') {
                                item.discount += ' OFF';
                            } else {
                                item.discount = `SAVE ${item.discount}`;
                            }
                        }
                    } else {
                        item.discount = getDiscount(parseInt(item.compareAtPrice), parseInt(item.memberPrice), locale);
                    }

                    item.memberPrice2 = formatPrice(item.memberPrice, locale);
                    item.price2 = formatPrice(item.price, locale);
                    item.compareAtPrice2 = formatPrice(item.compareAtPrice, locale);
                    item.memberPrice = formatPrice(item.memberPrice, locale);
                });

                setProductList(res.data);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return { productList, loading };
};

export const useUaualProductList = (module: ProductModule, locale: string) => {
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState<IProductItem[]>([]);

    useEffect(() => {
        getListUsualProducts(
            {
                module,
            },
            locale,
        )
            .then((res) => {
                if (res.code !== '0') return;
                res.data?.map((item: IProductItem) => {
                    if (SAVE_SITES.includes(locale)) {
                        if (parseInt(item.compareAtPrice) > parseInt(item.memberPrice)) {
                            item.discount = formatPrice(String(parseInt(item.compareAtPrice) - parseInt(item.memberPrice)), locale);
                            // 不同区域展示有差异
                            if (locale === 'za') {
                                item.discount += ' OFF';
                            } else {
                                item.discount = `SAVE ${item.discount}`;
                            }
                        }
                    } else {
                        item.discount = getDiscount(parseInt(item.compareAtPrice), parseInt(item.memberPrice), locale);
                    }

                    item.memberPrice2 = formatPrice(item.memberPrice, locale);
                    item.price2 = formatPrice(item.price, locale);
                    item.compareAtPrice2 = formatPrice(item.compareAtPrice, locale);
                });
                setProductList(res.data);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return { productList, loading };
};

export const useKrProductList = (module: ProductModule, locale: string) => {
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState<IProductItem[]>([]);

    useEffect(() => {
        getListProducts(
            {
                module,
            },
            locale,
        )
            .then((res) => {
                if (res.code !== '0') return;
                const dataSet = res.data?.map((data: IProductItem) => {
                    const item = { ...data };
                    if (parseInt(item.compareAtPrice) > parseInt(item.memberPrice)) {
                        item.discount = formatPrice(String(parseInt(item.compareAtPrice) - parseInt(item.memberPrice)), locale);
                        item.discount = `- ${item.discount}`;
                    }

                    item.memberPrice2 = formatPrice(item.memberPrice, locale);
                    item.price2 = formatPrice(item.price, locale);
                    item.compareAtPrice2 = formatPrice(item.compareAtPrice, locale);
                    item.memberPrice = formatPrice(item.memberPrice, locale);
                    return item;
                });

                setProductList(dataSet);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return { productList, loading };
};

export const useJpProductList = (module: ProductModule, locale: string) => {
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState<IProductItem2[][]>([]);

    useEffect(() => {
        getListProducts(
            {
                module,
            },
            locale,
        )
            .then((res) => {
                if (res.code !== '0') return;

                const dataSet = res.data?.map((data: IProductItem2) => {
                    const item = { ...data };

                    // if (parseInt(data.compareAtPrice) > parseInt(data.memberPrice)) {
                    //     item.discount = getDiscount(parseInt(item.compareAtPrice), parseInt(item.memberPrice), locale);
                    //     item.discount = `${item.discount} OFF`;
                    // }
                    if (item.discountPercent) {
                        item.discount = `${item.discountPercent}% OFF`;
                    }

                    item.memberPrice2 = formatPrice(item.memberPrice, locale);
                    item.price2 = formatPrice(item.price, locale);
                    // item.points = formatPrice(item.points, locale);
                    item.compareAtPrice2 = formatPrice(item.compareAtPrice, locale);
                    // item.memberPrice = formatPrice(item.memberPrice, locale);
                    item.points2 = new Intl.NumberFormat(i18nMap[locale]).format(Number(item.points));

                    return item;
                });
                const grouped = dataSet.reduce((acc: any, obj: IProductItem2) => {
                    const key = obj.productPosition;
                    if (!acc[key]) {
                        acc[key] = [];
                    }
                    acc[key].push(obj);
                    return acc;
                }, {});

                const newProductList = Object.values(grouped).map((group: any) => group);

                setProductList(newProductList);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return { productList, loading };
};

export const useEuProductList = (module: ProductModule, locale: string) => {
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState<IProductItem[]>([]);

    useEffect(() => {
        getListProducts(
            {
                module,
            },
            locale,
        )
            .then((res) => {
                if (res.code !== '0') return;
                res.data?.map((item: IProductItem) => {
                    item.discount = getDiscount(parseInt(item.compareAtPrice), parseInt(item.memberPrice), locale, true);

                    item.memberPrice2 = formatPrice(item.memberPrice, locale);
                    item.price2 = formatPrice(item.price, locale);
                    item.compareAtPrice2 = formatPrice(item.compareAtPrice, locale);
                });
                setProductList(res.data);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return { productList, loading };
};
